import React, { useState, useEffect, useMemo } from "react";
import Card from "components/card";
import { BsExclamationDiamond } from "react-icons/bs";
import { useSelectedDevice } from "context/SelectedDeviceContext";
import { collection, onSnapshot, query, where, updateDoc, doc } from "firebase/firestore";
import { db } from "services/firebase";
import { useAuth } from "context/AuthContext";

interface Solicitacao {
  id: string;
  dispositivoId: string;
  produto: string;
  data: string;
  hora: string;
  status: string;
}

export default function Solicitacoes() {
  const { user } = useAuth();
  const { selectedDeviceId } = useSelectedDevice();
  const [solicitacoes, setSolicitacoes] = useState<Solicitacao[]>([]);

  useEffect(() => {
    if (!user?.uid || !selectedDeviceId) {
      setSolicitacoes([]);
      return;
    }

    const collectionPath = `clientes/${user.uid}/solicitacoes`;
    const q = query(
      collection(db, collectionPath),
      where("dispositivoId", "==", selectedDeviceId)
    );

    const unsub = onSnapshot(q, snapshot => {
      const arr = snapshot.docs.map(docSnap => {
        const d = docSnap.data() as any;
        return {
          id: docSnap.id,
          dispositivoId: d.dispositivoId,
          produto: d.produto || d.Produto || "",
          data: d.data || d.Data || "",
          hora: d.hora || d.Hora || "",
          status: d.status || d.Status || "",
        };
      });
      setSolicitacoes(arr);
    });

    return () => unsub();
  }, [selectedDeviceId, user]);

  const solicitacoesOrdenadas = useMemo(() => {
    return [...solicitacoes].sort((a, b) => {
      const dateTimeA = `${a.data}T${a.hora}:00`;
      const dateTimeB = `${b.data}T${b.hora}:00`;
      
      return new Date(dateTimeB).getTime() - new Date(dateTimeA).getTime();
    });
  }, [solicitacoes]);

  const confirmarSolicitacao = async (id: string) => {
    if (!user?.uid) return;
    try {
      const docRef = doc(db, `clientes/${user.uid}/solicitacoes`, id);
      await updateDoc(docRef, { Status: "Confirmado" });
    } catch (error) {
      console.error("Erro ao confirmar solicitação:", error);
      alert("Erro ao confirmar solicitação.");
    }
  };

  const formatarData = (data: string) => {
    if (!data || !data.includes('-')) return data;
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  return (
    <Card extra="w-full p-4 h-full">
      <h4 className="text-xl font-bold text-navy-700 mb-4 shrink-0">
        Solicitações
      </h4>

      {!selectedDeviceId ? (
        <div className="flex flex-col items-center justify-center h-full text-gray-500">
          <div className="mt-2 flex items-center justify-center rounded-full bg-lightPrimary p-[26px] text-5xl font-bold text-[#CF4949]">
            <BsExclamationDiamond />
          </div>
          <h3 className="text-xl font-bold text-navy-700 my-3">Nada Aqui</h3>
          <p className="text-center text-sm text-gray-600 px-4">
            Selecione um <span className="font-semibold">Dispositivo</span> na lista de dispositivos para visualizar uma <span className="font-semibold">Solicitação</span>.
          </p>
        </div>
      ) : solicitacoesOrdenadas.length === 0 ? (
        <div className="flex items-center justify-center h-full text-center text-gray-500">
          Nenhuma solicitação para este dispositivo.
        </div>
      ) : (
        <div className="overflow-y-auto max-h-[280px]">
          <table className="w-full text-left">
            <thead>
              <tr className="text-sm font-bold text-gray-600">
                <th>Produto</th>
                <th>Data</th>
                <th>Hora</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {solicitacoesOrdenadas.map((s) => (
                <tr key={s.id} className="border-t">
                  <td className="py-2 text-sm font-bold text-navy-700">{s.produto}</td>
                  <td className="py-2 text-sm font-bold text-navy-700">{formatarData(s.data)}</td>
                  <td className="py-2 text-sm font-bold text-navy-700">{s.hora}</td>
                  <td className="py-2 text-sm font-bold text-navy-700">
                    <button
                      onClick={() => confirmarSolicitacao(s.id)}
                      disabled={s.status === "Confirmado"}
                      className={`text-sm font-bold ${
                        s.status === "Confirmado"
                          ? "text-green-500 cursor-default"
                          : "text-blue-500 hover:underline"
                      }`}
                    >
                      {s.status === "Confirmado" ? "Confirmado" : "Confirmar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
